<script context="module">
  // @ts-check
  /**
   * @typedef {Object} CalendarHeaderMonth
   * @property {string} label // e.g., "January"
   * @property {string} value // YYYY-MM-DD format (always uses the first of the month)
   */

  /**
   * @typedef {Object} CalendarHeaderYear
   * @property {string} label // e.g., "2026"
   * @property {CalendarHeaderMonth[]} months
   */
</script>

<script>
  // @ts-check

  import { DynamicHeading, DynamicHeadingContext, Icon } from "@ObamaFoundation/of-design-system";

  export let id = "calendar-header";

  /**
   * The title to display in the header
   * @type {string}
   */
  export let title = "";

  /**
   * The currently selected month in YYYY-MM-DD format
   * @type {string}
   */
  export let currentMonth = "";

  /**
   * Function to call when the month is changed
   * @type {(event: Event & { currentTarget: EventTarget & HTMLSelectElement; }) => void}
   */
  export let onMonthChange = () => {};

  /**
   * The list of years and months to display in the dropdown
   * @type {CalendarHeaderYear[]}
   */
  export let years = [];

  /**
   * Whether to hide the month dropdown
   * @type {boolean}
   */
  export let hideDropdown = false;

  const dropdownId = `${id}-dropdown`;
</script>

<DynamicHeadingContext>
  <div class="group relative md:w-fit">
    {#if !hideDropdown}
      <label for={dropdownId} class="sr-only">Select a month</label>
      <select
        id={dropdownId}
        bind:value={currentMonth}
        on:change={onMonthChange}
        class="absolute inset-0 rounded-none outline-2 outline-offset-4 focus-visible:outline-dashed outline-current"
      >
        {#each years as year}
          <optgroup class="text-grey label" label={year.label}>
            {#each year.months as month}
              <option
                class="first:mt-2 last:mb-2 checked:text-cobalt-blue text-black cta-md flex justify-start gap-x-1 py-2 px-2 outline-2 outline-offset-4 focus-visible:outline-dashed outline-black"
                value={month.value}>{month.label}</option
              >
            {/each}
          </optgroup>
        {/each}
      </select>
    {/if}
    <DynamicHeading
      class="pointer-events-none bg-white relative z-[var(--stack-level-schedule-list-title)] flex items-center gap-x-4 text-black heading-xl !gap-x-3 lg:!gap-x-6"
      suggestedHeadingLevel={2}
    >
      <span class="sm:max-md:grow">
        {title}
      </span>
      {#if !hideDropdown}
        <span
          class="group-has-[select:open]:-rotate-180 transition-transform duration-300 ease-in-out h-6 w-6 md:h-8 md:w-8 lg:h-14 lg:w-14 xl:h-15 xl:w-15"
        >
          <Icon name="caretDown" inheritSize={true} />
        </span>
      {/if}
    </DynamicHeading>
  </div>
</DynamicHeadingContext>

<style>
  /* Custom styles for the select dropdown and its picker */
  select,
  ::picker(select) {
    appearance: base-select;
  }

  ::picker(select) {
    background-color: rgba(243, 243, 238, 1);
    border: none;
    padding: 1rem 0.5rem;
    top: calc(anchor(bottom) + 20px);
    opacity: 0;
    transition: all 0.4s allow-discrete;
  }

  ::picker(select):popover-open {
    opacity: 1;
  }

  @starting-style {
    ::picker(select):popover-open {
      opacity: 0;
    }
  }

  option::checkmark {
    order: 1;
    content: "•";
  }
</style>
